import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ExternalLink, Star, Play } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const MockupSharePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState({ rating: 0, name: '', email: '', message: '' });
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  useEffect(() => {
    fetchSharePage();
  }, [token]);

  const fetchSharePage = async () => {
    try {
      const response = await axios.get(`${API}/client-mockups/public/share/${token}`);
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This mockup link is invalid or has been removed.');
      } else if (err.response?.status === 410) {
        setError('This mockup link has expired.');
      } else {
        setError('Failed to load mockup. Please contact us if this persists.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMockup = async () => {
    try {
      // Log click event
      await axios.post(`${API}/client-mockups/share/mockup/${token}/track`, null, {
        params: { event_type: 'OPEN_MOCKUP_CLICK' }
      });
    } catch (err) {
      console.error('Failed to track click:', err);
    }
    
    // Redirect to preview URL
    window.location.href = data.preview_url;
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/client-mockups/share/mockup/${token}/feedback`, feedback);
      setFeedbackSubmitted(true);
    } catch (err) {
      alert('Failed to submit feedback. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E0E0E] flex items-center justify-center">
        <div className="text-[#8A8A8A]">Loading mockup...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0E0E0E]">
        <div className="noise-overlay" aria-hidden="true" />
        <header className="border-b border-[#2E2E2E] bg-[#1C1C1C]/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
            <img src="/orchestrio-logo.png" alt="Orchestrio" className="h-6" />
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-2">Mockup Not Available</h2>
          <p className="text-[#8A8A8A]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0E0E]">
      <div className="noise-overlay" aria-hidden="true" />
      
      {/* Orchestrio Header */}
      <header className="border-b border-[#2E2E2E] bg-[#1C1C1C]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <img src="/orchestrio-logo.png" alt="Orchestrio" className="h-6" />
            <span className="text-[#8A8A8A] text-sm hidden sm:inline">Client Mockup Preview</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="space-y-8">
          {/* Title Section */}
          <div className="text-center space-y-3">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
              {data.client_name} — Website Concept Mockup
            </h1>
            <p className="text-base sm:text-lg text-[#8A8A8A]">
              {data.project_name} (v1)
            </p>
          </div>

          {/* Introduction Bullets */}
          <div className="card-dashboard bg-[#1C1C1C] border-[#2E2E2E]">
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-[#8A8A8A]">
                <span className="text-[#5F8FB8] mt-1 flex-shrink-0">•</span>
                <span>This is a concept mockup showing layout, structure, and style.</span>
              </li>
              <li className="flex items-start gap-3 text-[#8A8A8A]">
                <span className="text-[#5F8FB8] mt-1 flex-shrink-0">•</span>
                <span>Reply with up to 3 changes to refine it.</span>
              </li>
            </ul>
          </div>

          {/* Client Notes */}
          {data.notes && (
            <div className="card-dashboard bg-[#1C1C1C] border-[#2E2E2E]">
              <h3 className="text-base font-bold mb-3">Notes from Our Team</h3>
              <div className="text-[#8A8A8A] whitespace-pre-wrap">{data.notes}</div>
            </div>
          )}

          {/* Loom Video */}
          {data.loom_url && (
            <div className="card-dashboard bg-[#1C1C1C] border-[#2E2E2E]">
              <h3 className="text-base font-bold mb-3">Video Walkthrough</h3>
              <a
                href={data.loom_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#5F8FB8] hover:text-[#4A7A9F] text-sm font-medium"
              >
                <Play className="w-4 h-4" />
                Watch on Loom
              </a>
            </div>
          )}

          {/* Primary CTA - Open Mockup */}
          <div className="text-center py-6">
            <button
              onClick={handleOpenMockup}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#5F8FB8] hover:bg-[#4A7A9F] text-[#F5F4EF] font-medium rounded-lg transition-colors text-base sm:text-lg"
            >
              <ExternalLink className="w-5 h-5" />
              Open the mockup
            </button>
          </div>

          {/* Feedback Form */}
          <div className="card-dashboard bg-[#1C1C1C] border-[#2E2E2E]">
            <h3 className="text-base font-bold mb-4">Share Your Feedback</h3>
            
            {feedbackSubmitted ? (
              <div className="text-center py-8">
                <div className="text-green-400 text-5xl mb-3">✓</div>
                <h4 className="text-lg font-medium mb-2">Thank you for your feedback!</h4>
                <p className="text-[#8A8A8A] text-sm">We’ll be in touch soon to discuss next steps.</p>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                {/* Star Rating */}
                <div>
                  <label className="text-sm text-[#8A8A8A] mb-2 block">How would you rate this mockup?</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFeedback({...feedback, rating: star})}
                        className={`p-2 rounded transition-colors ${
                          feedback.rating >= star
                            ? 'text-yellow-400'
                            : 'text-[#2E2E2E] hover:text-[#8A8A8A]'
                        }`}
                      >
                        <Star
                          className="w-7 h-7 sm:w-8 sm:h-8"
                          fill={feedback.rating >= star ? 'currentColor' : 'none'}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name & Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={feedback.name}
                    onChange={(e) => setFeedback({...feedback, name: e.target.value})}
                    className="input-field bg-[#0E0E0E] border-[#2E2E2E] text-[#F5F4EF]"
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    value={feedback.email}
                    onChange={(e) => setFeedback({...feedback, email: e.target.value})}
                    className="input-field bg-[#0E0E0E] border-[#2E2E2E] text-[#F5F4EF]"
                  />
                </div>

                {/* Message */}
                <textarea
                  rows={4}
                  placeholder="Any comments or changes you’d like to see? (up to 3 changes recommended)"
                  value={feedback.message}
                  onChange={(e) => setFeedback({...feedback, message: e.target.value})}
                  className="input-field bg-[#0E0E0E] border-[#2E2E2E] text-[#F5F4EF] resize-none"
                />

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full sm:w-auto px-6 py-3 bg-[#5F8FB8] hover:bg-[#4A7A9F] text-[#F5F4EF] font-medium rounded-lg transition-colors"
                >
                  Submit Feedback
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#2E2E2E] mt-12 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-sm text-[#8A8A8A]">
          <div className="mb-2">
            Powered by <span className="text-[#F5F4EF] font-medium">Orchestrio</span>
          </div>
          <div className="text-xs">
            Digital systems studio • <a href="https://orchestrio.co.uk" className="text-[#5F8FB8] hover:underline">orchestrio.co.uk</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MockupSharePage;
